/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import { Box } from "@mui/material";
import React, { useState, useMemo } from "react";
import { css } from "aphrodite";
import { useNavigate } from "react-router-dom";
import DropDownSelect from "../../components/common/Dropdown/index";
import {
  MONTHS_OPTIONS,
  YEARS_OPTIONS,
  getCurrentMonth,
  getPreviousMonth,
  getCurrentYear,
} from "../../utils/dateAndTime";
import Typography from "../../components/common/new/Typography/Typography";
import InputField from "../../components/common/new/InputField/InputField";
import Colors from "../../theme/colors";
import Styles from "./styles";
import { isEmptyObj } from "../../utils/helper";
import Button from "../../components/common/Button";

// ── Calendar Icon ─────────────────────────────────────────────────
function CalendarIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      style={{ opacity: 0.7 }}
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

// ── Stat Tile ─────────────────────────────────────────────────────
const TILE_STYLES = {
  member: { background: "#424874", color: "#fff" },
  leader: { background: "#64748B", color: "#fff" },
  friend: { background: "#A6B1E1", color: "#2D2F45" },
  newFriend: { background: "#DCD6F7", color: "#484262" },
};

function StatTile({ tileKey, label, present, total }) {
  const { background, color } = TILE_STYLES[tileKey];
  return (
    <Box
      sx={{
        background,
        borderRadius: "10px",
        padding: "10px 12px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          color,
          opacity: 0.85,
          letterSpacing: "0.3px",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: "20px", fontWeight: 700, color, lineHeight: 1 }}
      >
        {present}
        <Typography
          component="span"
          sx={{ fontSize: "12px", fontWeight: 400, color, opacity: 0.6 }}
        >
          /{total}
        </Typography>
      </Typography>
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────────
function AttendanceHeaderForMobile({
  isArchive,
  attendanceList,
  meetings,
  otherAttendeesCount,
  onChangeOtherAttendeesCount,
  selectedMeetingType,
}) {
  const [month, setMonth] = useState(() =>
    isArchive ? getPreviousMonth(getCurrentMonth()) : getCurrentMonth(true)
  );
  const [year, setYear] = useState(getCurrentYear());
  const isGivenIdPresent = (id) => attendanceList.includes(id);
  const navaigate = useNavigate();

  const [presentFriends, presentLeaders, presentMembers, presentNewFriends] =
    useMemo(() => {
      if (meetings && !isEmptyObj(meetings)) {
        return [
          meetings?.friends?.length
            ? meetings.friends.filter((item) => isGivenIdPresent(item.friendId))
                .length
            : 0,
          meetings?.leaders?.length
            ? meetings.leaders.filter((item) =>
                isGivenIdPresent(`${item.memberId}-L`)
              ).length
            : 0,
          meetings?.members?.length
            ? meetings.members.filter((item) => isGivenIdPresent(item.memberId))
                .length
            : 0,
          meetings?.newFriends?.length
            ? meetings.newFriends.filter((item) =>
                isGivenIdPresent(item.friendId)
              ).length
            : 0,
        ];
      }
      return [0, 0, 0, 0];
    }, [meetings, isGivenIdPresent]);

  const totalFriendsCount = meetings?.friends?.length ?? 0;
  const totalMembersCount = meetings?.members?.length ?? 0;
  const totalNewFriendsCount = meetings?.newFriends?.length ?? 0;
  const totalLeadersCount = meetings?.leaders?.length ?? 0;

  const totalMeetingCount =
    totalFriendsCount +
    totalMembersCount +
    totalNewFriendsCount +
    totalLeadersCount;

  const presentTotalMeetingCount =
    presentFriends +
    presentLeaders +
    presentMembers +
    presentNewFriends +
    Number(otherAttendeesCount);

  const onAddNewFriendClick = () => navaigate("/friends/new-friend/search");

  const monthLabel =
    MONTHS_OPTIONS?.find((m) => m.value === month)?.label ?? month;

  return (
    <Box className={css(Styles.nonStickyBanner)}>
      {/* ══ GRADIENT HEADER ══════════════════════════════════════ */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg, #3730A3 0%, #4F46E5 50%, #6366F1 100%)",
          borderRadius: "14px",
          padding: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: "12px",
        }}
      >
        {/* Left: Meeting Type + Date chips */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            flex: 1,
            mr: "12px",
          }}
        >
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: "rgba(255,255,255,0.65)",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            Meeting Type
          </Typography>

          {/* Meeting Name Badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "20px",
              padding: "4px 12px",
              width: "fit-content",
              maxWidth: "100%",
            }}
          >
            <Box
              sx={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "#A5F3FC",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "0.3px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedMeetingType}
            </Typography>
          </Box>

          {/* Month + Year chips */}
          <Box sx={{ display: "flex", gap: "6px", mt: "2px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: "8px",
                padding: "4px 10px",
              }}
            >
              <CalendarIcon />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#fff",
                  textTransform: "uppercase",
                }}
              >
                {monthLabel}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: "8px",
                padding: "4px 10px",
              }}
            >
              <Typography
                sx={{ fontSize: "12px", fontWeight: 600, color: "#fff" }}
              >
                {year}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right: Total Badge */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "rgba(255,255,255,0.15)",
            border: "1.5px solid rgba(255,255,255,0.3)",
            borderRadius: "12px",
            padding: "10px 16px",
            minWidth: "68px",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: "rgba(255,255,255,0.7)",
              letterSpacing: "0.8px",
              textTransform: "uppercase",
            }}
          >
            Total
          </Typography>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.1,
            }}
          >
            {presentTotalMeetingCount}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>
            / {totalMeetingCount}
          </Typography>
        </Box>
      </Box>

      {/* ══ HIDDEN DROPDOWNS (state preserved) ══════════════════ */}
      <Box sx={{ display: "none" }}>
        <DropDownSelect
          title="Month"
          label="Month"
          selectOptions={MONTHS_OPTIONS}
          value={month}
          setValue={setMonth}
          style={{ width: "100%", color: Colors.black }}
          disabled
        />
        <DropDownSelect
          title="Year"
          label="Year"
          selectOptions={YEARS_OPTIONS}
          value={year}
          setValue={setYear}
          style={{ width: "100%", color: Colors.black }}
          disabled
        />
      </Box>

      {/* ══ STATS 2x2 GRID ═══════════════════════════════════════ */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px",
          mb: "12px",
        }}
      >
        <StatTile
          tileKey="member"
          label="Member"
          present={presentMembers}
          total={totalMembersCount}
        />
        <StatTile
          tileKey="leader"
          label="Leader"
          present={presentLeaders}
          total={totalLeadersCount}
        />
        <StatTile
          tileKey="friend"
          label="Friend"
          present={presentFriends}
          total={totalFriendsCount}
        />
        <StatTile
          tileKey="newFriend"
          label="New Friend"
          present={presentNewFriends}
          total={totalNewFriendsCount}
        />
      </Box>

      {/* ══ BOTTOM ACTIONS ═══════════════════════════════════════ */}
      <Box
        sx={{
          display: "flex",
          gap: "10px",
          alignItems: "center",
          borderTop: "1px solid #F1F3F6",
          pt: "12px",
          mb: "8px",
        }}
      >
        <InputField
          label="Other Members"
          value={otherAttendeesCount}
          onChange={onChangeOtherAttendeesCount}
          style={isArchive ? { width: "100%" } : { flex: 1 }}
          disabled={isArchive}
        />
        {!isArchive && (
          <Button
            variant="contained"
            label="+ Add New Friend"
            style={{
              flex: 1,
              height: "44px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #4F46E5, #3730A3)",
              fontWeight: 700,
              fontSize: "13px",
              padding: "0px",
              whiteSpace: "nowrap",
              boxShadow: "0 4px 12px rgba(79,70,229,0.35)",
            }}
            onClick={onAddNewFriendClick}
          />
        )}
      </Box>
    </Box>
  );
}

export default AttendanceHeaderForMobile;
